import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useGlobalAction } from '@gadgetinc/react';
import { api } from '../api';

export default function SetupPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordChecks, setPasswordChecks] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });
  const [isSuccess, setIsSuccess] = useState(false);

  const [{ data, fetching, error }, setupPassword] = useGlobalAction(api.setupPassword);

  useEffect(() => {
    if (!token) {
      setValidationErrors({ token: 'No setup token provided. Please check your email for the correct link.' });
    }
  }, [token]);

  useEffect(() => {
    if (data?.success) {
      setIsSuccess(true);
      setTimeout(() => {
        navigate(`/sign-in?email=${data.user?.email || ''}`);
      }, 3000);
    }
  }, [data, navigate]);

  const calculatePasswordStrength = (pwd) => {
    let strength = 0;
    const checks = {
      length: pwd.length >= 8,
      uppercase: /[A-Z]/.test(pwd),
      lowercase: /[a-z]/.test(pwd),
      number: /\d/.test(pwd),
      special: /[^\w\s]/.test(pwd)
    };
    
    // Debug logging
    console.log('Password being tested:', JSON.stringify(pwd));
    console.log('Password length:', pwd.length);
    console.log('Characters with codes:', pwd.split('').map(char => `${char}(${char.charCodeAt(0)})`).join(' '));
    console.log('Regex test results:');
    console.log('- length >= 8:', checks.length, pwd.length);
    console.log('- uppercase /[A-Z]/:', checks.uppercase, '/[A-Z]/.test(pwd) =', /[A-Z]/.test(pwd));
    console.log('- lowercase /[a-z]/:', checks.lowercase, '/[a-z]/.test(pwd) =', /[a-z]/.test(pwd));
    console.log('- number /\\d/:', checks.number, '/\\d/.test(pwd) =', /\d/.test(pwd));
    console.log('- special /[!@#$%^&*(),.?":{}|<>]/:', checks.special, '/[!@#$%^&*(),.?":{}|<>]/.test(pwd) =', /[!@#$%^&*(),.?":{}|<>]/.test(pwd));
    console.log('Numbers found by number regex:', pwd.match(/\d/g));
    console.log('Individual digit tests:', '0123456789'.split('').map(digit => `${digit}: ${pwd.includes(digit)}`));
    
    strength = Object.values(checks).filter(Boolean).length;
    return { strength, checks };
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    
    const { strength, checks } = calculatePasswordStrength(newPassword);
    setPasswordStrength(strength);
    setPasswordChecks(checks);
    
    // Clear password validation errors
    if (validationErrors.password) {
      setValidationErrors(prev => ({ ...prev, password: '' }));
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);
    
    // Clear confirm password validation errors
    if (validationErrors.confirmPassword) {
      setValidationErrors(prev => ({ ...prev, confirmPassword: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 8) {
      errors.password = 'Password must be at least 8 characters long';
    } else if (passwordStrength < 5) {
      errors.password = 'Password must meet all requirements: uppercase, lowercase, numbers, and special characters.';
    }
    
    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!token) {
      setValidationErrors({ token: 'Invalid setup token' });
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    try {
      await setupPassword({ token, password });
    } catch (err) {
      setValidationErrors({ submit: err.message || 'Failed to set password' });
    }
  };

  const getStrengthColor = (strength) => {
    if (strength < 2) return '#ef4444'; // red
    if (strength < 4) return '#f59e0b'; // yellow
    return '#10b981'; // green
  };

  const getStrengthText = (strength) => {
    if (strength < 2) return 'Weak';
    if (strength < 4) return 'Medium';
    return 'Strong';
  };

  if (isSuccess) {
    return (
      <div className="auth-page">
        <div className="auth-card success-animation">
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h2>Password Set Successfully!</h2>
            <p>Your password has been set. You will be redirected to sign in in a few seconds.</p>
            <Link to={`/sign-in?email=${data?.user?.email || ''}`} className="link-primary">
              Click here if you're not redirected automatically
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Set Your Password</h1>
          <p>Please create a secure password for your account.</p>
        </div>

        {(validationErrors.token || error) && (
          <div className="error-alert">
            <span className="error-icon">⚠</span>
            <span>{validationErrors.token || error?.message || 'An error occurred'}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="password" className="form-label">
              New Password *
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={handlePasswordChange}
              placeholder="Enter your new password"
              disabled={fetching || !token}
              className={`form-input ${validationErrors.password ? 'error' : ''}`}
              required
            />
            
            {password && (
              <>
                <div className="password-strength">
                  <div className="strength-bar">
                    <div 
                      className="strength-fill"
                      style={{ 
                        width: `${(passwordStrength / 5) * 100}%`,
                        backgroundColor: getStrengthColor(passwordStrength)
                      }}
                    ></div>
                  </div>
                  <span 
                    className="strength-text"
                    style={{ color: getStrengthColor(passwordStrength) }}
                  >
                    {getStrengthText(passwordStrength)}
                  </span>
                </div>
                
                <div className="password-requirements">
                  <div className={`requirement ${passwordChecks.length ? 'met' : 'unmet'}`}>
                    <span className="requirement-icon">
                      {passwordChecks.length ? '✓' : '○'}
                    </span>
                    <span className="requirement-text">At least 8 characters</span>
                  </div>
                  <div className={`requirement ${passwordChecks.uppercase ? 'met' : 'unmet'}`}>
                    <span className="requirement-icon">
                      {passwordChecks.uppercase ? '✓' : '○'}
                    </span>
                    <span className="requirement-text">Contains uppercase letter (A-Z)</span>
                  </div>
                  <div className={`requirement ${passwordChecks.lowercase ? 'met' : 'unmet'}`}>
                    <span className="requirement-icon">
                      {passwordChecks.lowercase ? '✓' : '○'}
                    </span>
                    <span className="requirement-text">Contains lowercase letter (a-z)</span>
                  </div>
                  <div className={`requirement ${passwordChecks.number ? 'met' : 'unmet'}`}>
                    <span className="requirement-icon">
                      {passwordChecks.number ? '✓' : '○'}
                    </span>
                    <span className="requirement-text">Contains number (0-9)</span>
                  </div>
                  <div className={`requirement ${passwordChecks.special ? 'met' : 'unmet'}`}>
                    <span className="requirement-icon">
                      {passwordChecks.special ? '✓' : '○'}
                    </span>
                    <span className="requirement-text">Contains special character (any symbol)</span>
                  </div>
                </div>
              </>
            )}
            
            {validationErrors.password && (
              <span className="field-error">{validationErrors.password}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">
              Confirm Password *
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              placeholder="Confirm your new password"
              disabled={fetching || !token}
              className={`form-input ${validationErrors.confirmPassword ? 'error' : ''}`}
              required
            />
            {validationErrors.confirmPassword && (
              <span className="field-error">{validationErrors.confirmPassword}</span>
            )}
          </div>

          <button
            type="submit"
            disabled={!token || !password || !confirmPassword || fetching}
            className={`btn-primary ${fetching ? 'loading' : ''}`}
          >
            {fetching ? (
              <>
                <span className="loading-spinner"></span>
                Setting Password...
              </>
            ) : (
              'Set Password'
            )}
          </button>

          {validationErrors.submit && (
            <div className="error-alert">
              <span className="error-icon">⚠</span>
              <span>{validationErrors.submit}</span>
            </div>
          )}
        </form>

        <div className="auth-footer">
          <p>
            Already have an account?{' '}
            <Link to="/sign-in" className="link-primary">
              Sign in here
            </Link>
          </p>
        </div>
      </div>

      <style jsx>{`
        .auth-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem 1rem;
        }

        .auth-card {
          background: white;
          border-radius: 16px;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          padding: 3rem 2rem;
          width: 100%;
          max-width: 450px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
        }

        .auth-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 25px 35px -5px rgba(0, 0, 0, 0.15), 0 15px 15px -5px rgba(0, 0, 0, 0.08);
        }

        .success-animation {
          animation: successPulse 0.6s ease-out;
        }

        @keyframes successPulse {
          0% { transform: scale(0.95); opacity: 0; }
          50% { transform: scale(1.02); }
          100% { transform: scale(1); opacity: 1; }
        }

        .auth-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .auth-header h1 {
          font-size: 2rem;
          font-weight: 700;
          color: #1a365d;
          margin-bottom: 0.5rem;
          letter-spacing: -0.025em;
        }

        .auth-header p {
          color: #718096;
          font-size: 1rem;
          line-height: 1.5;
        }

        .auth-form {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-label {
          font-weight: 600;
          color: #2d3748;
          font-size: 0.875rem;
        }

        .form-input {
          padding: 0.875rem 1rem;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          font-size: 1rem;
          transition: all 0.2s ease;
          background: white;
        }

        .form-input:focus {
          outline: none;
          border-color: #4299e1;
          box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
        }

        .form-input.error {
          border-color: #e53e3e;
          box-shadow: 0 0 0 3px rgba(229, 62, 62, 0.1);
        }

        .form-input:disabled {
          background-color: #f7fafc;
          color: #a0aec0;
          cursor: not-allowed;
        }

        .password-strength {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-top: 0.5rem;
        }

        .strength-bar {
          flex: 1;
          height: 4px;
          background-color: #e2e8f0;
          border-radius: 2px;
          overflow: hidden;
        }

        .strength-fill {
          height: 100%;
          border-radius: 2px;
          transition: all 0.3s ease;
        }

        .strength-text {
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.025em;
          min-width: 60px;
        }

        .password-requirements {
          margin-top: 1rem;
          padding: 1rem;
          background-color: #f7fafc;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }

        .requirement {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
          font-size: 0.875rem;
        }

        .requirement:last-child {
          margin-bottom: 0;
        }

        .requirement.met {
          color: #38a169;
        }

        .requirement.unmet {
          color: #a0aec0;
        }

        .requirement-icon {
          font-weight: bold;
          font-size: 0.75rem;
          width: 12px;
          text-align: center;
          flex-shrink: 0;
        }

        .requirement.met .requirement-icon {
          color: #38a169;
        }

        .requirement.unmet .requirement-icon {
          color: #cbd5e0;
        }

        .requirement-text {
          font-weight: 500;
        }

        .field-error {
          color: #e53e3e;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .btn-primary {
          background: linear-gradient(135deg, #4299e1 0%, #3182ce 100%);
          color: white;
          border: none;
          padding: 1rem 1.5rem;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          min-height: 52px;
        }

        .btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #3182ce 0%, #2c5aa0 100%);
          transform: translateY(-1px);
          box-shadow: 0 10px 20px -5px rgba(66, 153, 225, 0.4);
        }

        .btn-primary:active:not(:disabled) {
          transform: translateY(0);
        }

        .btn-primary:disabled {
          background: #a0aec0;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        .btn-primary.loading {
          cursor: not-allowed;
        }

        .loading-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid transparent;
          border-top: 2px solid currentColor;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .error-alert {
          background-color: #fed7d7;
          border: 1px solid #feb2b2;
          color: #c53030;
          padding: 1rem;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
        }

        .error-icon {
          font-weight: bold;
          flex-shrink: 0;
        }

        .success-message {
          text-align: center;
        }

        .success-icon {
          font-size: 3rem;
          color: #38a169;
          margin-bottom: 1rem;
          display: inline-block;
          animation: successBounce 0.8s ease-out;
        }

        @keyframes successBounce {
          0%, 20%, 53%, 80%, 100% { transform: translate3d(0, 0, 0); }
          40%, 43% { transform: translate3d(0, -10px, 0); }
          70% { transform: translate3d(0, -5px, 0); }
          90% { transform: translate3d(0, -2px, 0); }
        }

        .success-message h2 {
          color: #1a365d;
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 1rem;
        }

        .success-message p {
          color: #718096;
          margin-bottom: 1.5rem;
          line-height: 1.6;
        }

        .auth-footer {
          text-align: center;
          margin-top: 2rem;
          padding-top: 2rem;
          border-top: 1px solid #e2e8f0;
        }

        .auth-footer p {
          color: #718096;
          font-size: 0.875rem;
        }

        .link-primary {
          color: #4299e1;
          text-decoration: none;
          font-weight: 600;
          transition: color 0.2s ease;
        }

        .link-primary:hover {
          color: #3182ce;
          text-decoration: underline;
        }

        @media (max-width: 640px) {
          .auth-page {
            padding: 1rem 0.5rem;
          }
          
          .auth-card {
            padding: 2rem 1.5rem;
          }
          
          .auth-header h1 {
            font-size: 1.75rem;
          }
        }
      `}</style>
    </div>
  );
}